// src/pages/Partenaires/PartenaireDrawer.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FiCheck,
  FiCopy,
  FiExternalLink,
  FiImage,
  FiLink,
  FiSave,
  FiToggleLeft,
  FiUpload,
  FiX,
} from "react-icons/fi";

const FALLBACK_LOGO = "/img/partenaire-placeholder.png";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950";

const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (String(photoUrl).startsWith("http")) return photoUrl;

  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const clean = String(photoUrl).replaceAll("\\", "/").replace(/^\/+/, "");

  return `${base}/${clean}`;
};

export default function PartenaireDrawer({
  open,
  mode = "create",
  initial,
  onClose,
  onSave,
}) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    nom: "",
    link_url: "",
    photo_url: "",
    is_active: true,
  });

  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");

  const existingPhotoRaw = initial?.photo_url || "";

  const photoUrlFromForm = String(form.photo_url || "").trim();

  const previewFromUrl = useMemo(() => {
    return toAbsolutePhotoUrl(photoUrlFromForm);
  }, [photoUrlFromForm]);

  const existingLogo = useMemo(() => {
    return toAbsolutePhotoUrl(initial?.photo_url);
  }, [initial]);

  const previewLogo = filePreview || previewFromUrl || existingLogo || FALLBACK_LOGO;

  const hasExistingPhoto = Boolean(existingPhotoRaw);
  const hasTypedPhotoUrl = Boolean(photoUrlFromForm);

  useEffect(() => {
    if (!open) return;

    setForm({
      nom: initial?.nom || "",
      link_url: initial?.link_url || "",
      photo_url: initial?.photo_url || "",
      is_active:
        initial?.is_active === undefined ||
        initial?.is_active === null ||
        initial?.is_active === true ||
        initial?.is_active === 1 ||
        initial?.is_active === "1",
    });

    setFile(null);
    setFilePreview("");
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!file) {
      setFilePreview("");
      return;
    }

    const url = URL.createObjectURL(file);
    setFilePreview(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!open) return null;

  const copyToClipboard = async (value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch {
      window.prompt("Copier l’URL :", value);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    onSave(
      {
        nom: form.nom,
        link_url: form.link_url,
        photo_url: form.photo_url,
        is_active: form.is_active,
      },
      file
    );
  };

  return (
    <div className="fixed inset-0 z-[9999]">
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-950">
        <div className="relative overflow-hidden bg-slate-950 p-5 text-white sm:p-6">
          <div className="absolute -right-14 -top-14 h-40 w-40 rounded-full bg-indigo-500/30 blur-3xl" />
          <div className="absolute -bottom-16 left-1/3 h-40 w-40 rounded-full bg-pink-500/20 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-xl">
                <FiImage />
              </div>

              <h2 className="text-2xl font-bold tracking-tight">
                {isEdit ? "Modifier le partenaire" : "Ajouter un partenaire"}
              </h2>

              <p className="mt-1 text-sm leading-relaxed text-slate-300">
                {isEdit
                  ? "Mettez à jour le nom, le lien, le logo et la visibilité."
                  : "Ajoutez un logo et un lien pour vos sponsors ou partenaires."}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Fermer"
            >
              <FiX />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6">
          <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="relative h-56 overflow-hidden bg-white dark:bg-slate-950">
              <div className="flex h-full w-full items-center justify-center px-8 py-8">
                <img
                  src={previewLogo}
                  alt={form.nom || "Aperçu partenaire"}
                  className="max-h-full max-w-full object-contain"
                  onError={(event) => {
                    event.currentTarget.src = FALLBACK_LOGO;
                  }}
                />
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent pointer-events-none" />

              <div className="absolute bottom-4 left-4 right-4">
                <div className="inline-flex rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-slate-800 backdrop-blur">
                  Aperçu du logo
                </div>

                <h3 className="mt-3 line-clamp-2 text-2xl font-bold text-white drop-shadow">
                  {form.nom || "Nom du partenaire"}
                </h3>
              </div>
            </div>

            <div className="border-t border-slate-200 p-4 dark:border-slate-800">
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {previewLogo && previewLogo !== FALLBACK_LOGO ? (
                    <a
                      href={previewLogo}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 transition hover:bg-indigo-100 dark:bg-indigo-950/70 dark:text-indigo-300 dark:hover:bg-indigo-950"
                    >
                      <FiExternalLink />
                      Voir l’image affichée
                    </a>
                  ) : null}

                  {filePreview ? (
                    <span className="inline-flex items-center gap-2 rounded-2xl bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300">
                      <FiCheck />
                      Nouveau logo sélectionné
                    </span>
                  ) : null}
                </div>

                {/* {hasExistingPhoto ? (
                  <UrlDisplay
                    label="URL photo enregistrée"
                    value={existingPhotoRaw}
                    onCopy={copyToClipboard}
                  />
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-3 text-xs font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-400">
                    Aucune URL photo enregistrée pour ce partenaire.
                  </div>
                )} */}

                {previewLogo &&
                previewLogo !== existingPhotoRaw &&
                previewLogo !== FALLBACK_LOGO ? (
                  <UrlDisplay
                    label="URL complète utilisée pour l’aperçu"
                    value={previewLogo}
                    onCopy={copyToClipboard}
                  />
                ) : null}

                {file ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-xs font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300">
                    Nouveau fichier sélectionné : {file.name}
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          <div className="mt-5 grid grid-cols-1 gap-4">
            <Field label="Nom du partenaire" icon={FiImage} required>
              <input
                className={INPUT}
                value={form.nom}
                name="nom"
                onChange={handleChange}
                placeholder="Ex : Boutique Judo, sponsor local..."
                required
              />
            </Field>

            <Field label="Lien du partenaire" icon={FiLink}>
              <input
                className={INPUT}
                value={form.link_url}
                name="link_url"
                onChange={handleChange}
                placeholder="https://..."
              />

              {form.link_url ? (
                <a
                  href={form.link_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-2 text-xs font-bold text-indigo-600 hover:underline dark:text-indigo-300"
                >
                  <FiExternalLink />
                  Tester le lien
                </a>
              ) : (
                <p className="mt-2 text-xs font-medium text-slate-400">
                  Ce lien sera utilisé sur la page publique de la compétition.
                </p>
              )}
            </Field>

            <Field label="URL du logo" icon={FiLink}>
              <input
                className={INPUT}
                value={form.photo_url}
                name="photo_url"
                onChange={handleChange}
                placeholder="https://www.exemple.fr/logo.png"
              />

              <div className="mt-2 flex flex-wrap gap-3">
                {hasTypedPhotoUrl ? (
                  <>
                    <a
                      href={toAbsolutePhotoUrl(form.photo_url)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-xs font-bold text-indigo-600 hover:underline dark:text-indigo-300"
                    >
                      <FiExternalLink />
                      Tester l’image
                    </a>

                    <button
                      type="button"
                      onClick={() => copyToClipboard(form.photo_url)}
                      className="inline-flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"
                    >
                      <FiCopy />
                      Copier l’URL
                    </button>
                  </>
                ) : (
                  <p className="text-xs font-medium text-slate-400">
                    Vous pouvez renseigner une URL directe vers une image ou
                    sélectionner un fichier ci-dessous.
                  </p>
                )}
              </div>
            </Field>

            <Field label="Logo fichier" icon={FiUpload}>
              <input
                type="file"
                accept="image/*"
                className={INPUT}
                onChange={handleFileChange}
              />

              <p className="mt-2 text-xs font-medium text-slate-400">
                Optionnel : si vous sélectionnez un fichier, il remplacera
                l’URL du logo après envoi.
              </p>
            </Field>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
              <button
                type="button"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    is_active: !prev.is_active,
                  }))
                }
                className="flex w-full items-center justify-between gap-4 text-left"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                      form.is_active
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300"
                        : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                    }`}
                  >
                    <FiToggleLeft />
                  </div>

                  <div>
                    <div className="font-bold text-slate-900 dark:text-white">
                      Partenaire actif
                    </div>

                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Un partenaire actif peut être affiché dans les espaces
                      publics liés aux compétitions.
                    </p>
                  </div>
                </div>

                <span
                  className={`flex h-7 w-12 shrink-0 items-center rounded-full p-1 transition ${
                    form.is_active
                      ? "bg-emerald-600"
                      : "bg-slate-300 dark:bg-slate-700"
                  }`}
                >
                  <span
                    className={`h-5 w-5 rounded-full bg-white shadow transition ${
                      form.is_active ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </span>
              </button>
            </div>
          </div>
        </form>

        <div className="shrink-0 border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <FiX />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-pink-500 px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
            >
              <FiSave />
              {isEdit ? "Enregistrer" : "Créer"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, icon: Icon, children, required = false }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
        <Icon className="text-indigo-500" />
        {label}
        {required ? <span className="text-rose-500">*</span> : null}
      </span>

      {children}
    </label>
  );
}

function UrlDisplay({ label, value, onCopy }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/60">
      <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">
        {label}
      </div>

      <div className="flex items-start gap-2">
        <p className="min-w-0 flex-1 break-all text-xs font-medium leading-relaxed text-slate-600 dark:text-slate-300">
          {value}
        </p>

        <button
          type="button"
          onClick={() => onCopy(value)}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white text-slate-600 shadow-sm transition hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          title="Copier l’URL"
        >
          <FiCopy />
        </button>
      </div>
    </div>
  );
}